import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Users } from "lucide-react";

const isSamePresent = (s) => s.reason.startsWith("Già presente") && !s.reason.includes("diverso");

const PreviewBody = ({ p }) => {
  const same = p.skipped.filter(isSamePresent);
  const rest = p.skipped.filter((s) => !isSamePresent(s));
  return (
  <div className="space-y-3 text-sm">
    <p data-testid="carry-preview-total" className="text-base">Verranno portati <strong>{p.to_carry}</strong> allievi da {p.from_year} a {p.to_year}.</p>
    {p.filled_empty > 0 && <p className="text-xs text-muted-foreground" data-testid="carry-preview-filled">Di questi, {p.filled_empty} avevano nell'anno di arrivo solo un'iscrizione vuota creata in automatico: verrà completata con il corso.</p>}
    {p.by_course.length > 0 && (
      <ul className="divide-y divide-border rounded-lg border border-border" data-testid="carry-preview-courses">
        {p.by_course.map((c) => <li key={c.course} className="flex justify-between gap-3 p-2"><span className="break-words min-w-0">{c.course}</span><strong className="shrink-0">{c.count}</strong></li>)}
      </ul>
    )}
    <p data-testid="carry-preview-skipped-count">Saltati: <strong>{p.skipped_count}</strong> · Senza corso (non portati): <strong>{p.no_course_count}</strong></p>
    {same.length > 0 && <p className="text-xs text-muted-foreground" data-testid="carry-preview-same">{same.length} già presenti nell'anno {p.to_year} con lo stesso corso: non vengono toccati.</p>}
    {rest.length > 0 && (
      <ul className="max-h-52 overflow-y-auto divide-y divide-border rounded-lg border border-border text-xs" data-testid="carry-preview-skipped">
        {rest.map((s, i) => (
          <li key={i} className="p-2"><span className="font-medium break-words">{s.name || s.email || "—"}</span> <span className="text-muted-foreground">· {s.course}</span>
            <div className="text-amber-700">{s.reason}</div></li>
        ))}
      </ul>
    )}
  </div>
  );
};

// Anteprima obbligatoria, conferma con i numeri; per l'anno corrente anche il nome digitato
export const CarryDialog = ({ open, onOpenChange, fromYear, toYear, onDone }) => {
  const [p, setP] = useState(null);
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setP(null); setStep(1); setTyped("");
    api.post("/admin/carry/preview", { from_year: fromYear, to_year: toYear }).then((r) => setP(r.data))
      .catch((err) => { toast.error(apiError(err.response?.data?.detail)); onOpenChange(false); });
  }, [open, fromYear, toYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/carry", { from_year: fromYear, to_year: toYear, expected: p.to_carry, confirm_name: typed });
      toast.success(`${data.carried} allievi portati nell'anno ${toYear}`); onDone(data); onOpenChange(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const last = p?.is_current_target ? 3 : 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="carry-dialog" className="max-h-[90vh] overflow-y-auto w-[calc(100%-1.5rem)] rounded-lg">
        <DialogHeader><DialogTitle>Porta gli allievi nell'anno {toYear}</DialogTitle>
          <DialogDescription>Si crea solo una nuova iscrizione con lo stesso corso. L'anno {fromYear} e gli account non vengono toccati. Nessuna email o notifica.</DialogDescription>
        </DialogHeader>
        {!p ? <p className="text-sm text-muted-foreground">Calcolo anteprima…</p> : (
          <>
            {p.is_current_target && (
              <div data-testid="carry-current-warning" className="rounded-lg border-2 border-destructive bg-destructive/10 p-3 text-sm font-semibold text-destructive flex gap-2">
                <AlertTriangle size={18} className="shrink-0" /> {toYear} è l'anno CORRENTE: gli allievi portati vedranno subito il nuovo corso nella loro area.
              </div>
            )}
            {step === 1 && <PreviewBody p={p} />}
            {step === 2 && <p className="text-sm" data-testid="carry-confirm-text">Confermi di portare <strong>{p.to_carry}</strong> allievi da {p.from_year} a {p.to_year} ({p.skipped_count} saltati)?</p>}
            {step === 3 && (
              <div className="space-y-2">
                <Label className="text-sm">Per confermare digita il nome esatto dell'anno: <strong>{toYear}</strong></Label>
                <Input data-testid="carry-typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={toYear} className="bg-white" autoComplete="off" />
              </div>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid="carry-cancel" onClick={() => onOpenChange(false)}>Annulla</Button>
          {step < last
            ? <Button data-testid="carry-next" disabled={!p || p.to_carry === 0} onClick={() => setStep(step + 1)}>{p?.to_carry === 0 ? "Nessuno da portare" : "Continua"}</Button>
            : <Button data-testid="carry-apply" disabled={busy || (p?.is_current_target && typed !== toYear)} onClick={apply}><Users size={16} className="mr-1.5" /> Porta {p?.to_carry} allievi</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
